import { AlertTriangle, CheckCircle2, Gauge, RotateCcw } from 'lucide-react';

export interface ExecutionQualityCardProps {
    outcome: 'completed' | 'partial' | 'failed';
    counts?: {
        total?: number | null;
        completed?: number | null;
        failed?: number | null;
        blocked?: number | null;
        pending?: number | null;
    };
    quality?: {
        mutatingAttempted?: number | null;
        failedMutationNoEffect?: number | null;
        mutationNoEffectRate?: number | null;
        repairAttempts?: number | null;
        retryableSteps?: number | null;
        nonBlockingSkippedSteps?: number | null;
        dominantFailureClass?: string;
        lowQuality?: boolean;
        lowQualityReason?: string;
    };
    recommendedAction?: string;
}

function formatPercent(value?: number | null): string {
    if (typeof value !== 'number' || !Number.isFinite(value)) return 'n/a';
    return `${Math.round(value * 100)}%`;
}

function normalizeCount(value?: number | null): string {
    if (typeof value !== 'number' || !Number.isFinite(value)) return 'n/a';
    return value.toString();
}

function normalizeRawKey(value?: string | null): string {
    if (!value) return '';
    return value
        .replace(/_/g, ' ')
        .trim()
        .replace(/\s+/g, ' ')
        .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function formatFailureClass(value?: string): string {
    if (!value) return '';
    const key = value.toLowerCase();
    if (key === 'none' || key === 'unknown' || key === 'n/a') return '';
    if (key === 'infra_transient') return 'Temporary environment issue';
    if (key === 'edit_target_mismatch') return 'File changed during execution';
    if (key === 'command_timeout') return 'Command timed out';
    if (key === 'command_syntax_error') return 'Syntax error in changed code';
    if (key === 'dependency_failed') return 'Dependency issue';
    if (key === 'mutation_no_effect') return 'No effective code change';
    if (key === 'step_contract_invalid') return 'Internal step format issue';
    return normalizeRawKey(value);
}

function formatLowQualityReason(value?: string): string {
    if (!value) return 'Execution needs attention.';
    const key = value.toLowerCase();
    if (key === 'high_unresolved_ratio') return 'Too many steps remain unresolved.';
    if (key === 'high_blocked_ratio') return 'Too many steps were blocked by dependencies.';
    if (key === 'high_mutation_no_effect_rate') return 'Too many edit attempts did not change files.';
    if (key === 'infra_collapse_partial') return 'Execution was interrupted by environment/setup issues.';
    if (key === 'mutation_no_effect') return 'Edits did not apply as expected.';
    return normalizeRawKey(value);
}

function formatRecommendedAction(value?: string): string {
    if (!value) return '';
    const key = value.toLowerCase();
    if (key === 'continue_discussion' || key === 'resume_with_context') return '';
    if (key === 'retry_all_failed') return 'Retry failed steps';
    if (key === 'retry_with_feedback') return 'Retry with additional guidance';
    if (key === 'skip_remaining_steps') return 'Continue with successful changes';
    if (key === 'continue_to_next_batch') return 'Continue without creating a PR';
    if (key === 'create_pr_and_continue_to_next_batch') return 'Create PR and continue';
    if (key === 'skip_remaining_and_continue_next_batch_no_pr') return 'Continue without creating a PR';
    if (key === 'skip_all_failed') return 'Continue and skip failed steps';
    if (key === 'create_pr') return 'Create pull request';
    return normalizeRawKey(value);
}

export default function ExecutionQualityCard({
    outcome,
    counts,
    quality,
    recommendedAction,
}: ExecutionQualityCardProps) {
    const isLowQuality = quality?.lowQuality === true;
    const hasNoEffectData =
        typeof quality?.failedMutationNoEffect === 'number' &&
        quality.failedMutationNoEffect > 0;
    const noEffectRateLabel = formatPercent(quality?.mutationNoEffectRate);
    const failureClassLabel = formatFailureClass(quality?.dominantFailureClass);
    const lowQualityReasonLabel = formatLowQualityReason(quality?.lowQualityReason);
    const recommendedActionLabel = formatRecommendedAction(recommendedAction);
    const outcomeLabel = outcome === 'completed' ? 'Completed' : outcome === 'partial' ? 'Partial' : 'Failed';
    const outcomeColor = outcome === 'completed'
        ? 'text-emerald-600 dark:text-emerald-400'
        : outcome === 'partial'
            ? 'text-amber-600 dark:text-amber-300'
            : 'text-rose-600 dark:text-rose-400';

    return (
        <div className="rounded-lg bg-card p-3 space-y-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Gauge className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-semibold">Execution Quality</span>
                </div>
                <span className={`text-xs font-semibold ${outcomeColor}`}>{outcomeLabel}</span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                <div className="rounded-md bg-muted/40 p-2">
                    <div className="text-muted-foreground">Completed</div>
                    <div className="font-semibold">{normalizeCount(counts?.completed)}</div>
                </div>
                <div className="rounded-md bg-muted/40 p-2">
                    <div className="text-muted-foreground">Failed</div>
                    <div className="font-semibold">{normalizeCount(counts?.failed)}</div>
                </div>
                <div className="rounded-md bg-muted/40 p-2">
                    <div className="text-muted-foreground">Blocked</div>
                    <div className="font-semibold">{normalizeCount(counts?.blocked)}</div>
                </div>
                <div className="rounded-md bg-muted/40 p-2">
                    <div className="text-muted-foreground">Edit attempts</div>
                    <div className="font-semibold">{normalizeCount(quality?.mutatingAttempted)}</div>
                </div>
                <div className="rounded-md bg-muted/40 p-2">
                    <div className="text-muted-foreground">Repairs</div>
                    <div className="font-semibold">{normalizeCount(quality?.repairAttempts)}</div>
                </div>
            </div>

            <div className={`rounded p-2 text-xs ${isLowQuality ? 'bg-amber-500/10' : 'bg-emerald-500/10'}`}>
                <div className="flex items-center gap-2">
                    {isLowQuality ? (
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                    ) : (
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                    )}
                    <span className="font-medium">
                        {isLowQuality ? 'Low-quality execution detected' : 'Execution quality within acceptable range'}
                    </span>
                </div>
                {isLowQuality && (
                    <div className="mt-1 text-muted-foreground">
                        {lowQualityReasonLabel}
                    </div>
                )}
            </div>

            {isLowQuality && (failureClassLabel || hasNoEffectData) && (
                <div className="text-xs text-muted-foreground space-y-1">
                    {failureClassLabel && (
                        <div>Issue type: {failureClassLabel}</div>
                    )}
                    {hasNoEffectData && (
                        <div>
                            Unchanged edit attempts: {normalizeCount(quality?.failedMutationNoEffect)}
                            {noEffectRateLabel !== 'n/a' ? ` (${noEffectRateLabel})` : ''}
                        </div>
                    )}
                </div>
            )}

            {isLowQuality && recommendedActionLabel && (
                <div className="flex items-center gap-2 text-xs">
                    <RotateCcw className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="font-medium">Recommended action:</span>
                    <span className="rounded bg-muted px-1.5 py-0.5">{recommendedActionLabel}</span>
                </div>
            )}
        </div>
    );
}
